import React, { useState, useEffect } from "react";
import {
  X,
  FileBadge,
  CheckCircle,
  Loader,
  PauseCircle,
  UserCheck,
  RotateCcw,
  CircleSlash,
} from "lucide-react";
import frameBg from "../../../../../Assets/FrameBGDashboard.png";
import { ADInProgressTask } from "../Service/AllTaskService";
import NotificationAlert from "../../../../Alerts/NotificationAlert";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import {
  flatpickrScheduleDateDisable,
  getNextAllowedScheduleDate,
  isBlockedScheduleDate,
} from "../../../../../utlis/flatpickrDateRules";

const ADInProgressTaskModal = ({ show, onClose, taskData, onInProgress }) => {
  const [remark, setRemark] = useState("");
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useTaskExpectedDate, setUseTaskExpectedDate] = useState(false);
  const [expectedDate, setExpectedDate] = useState("");

  const isFutureDateTime = (date) => {
    if (!date) return false;
    return date.getTime() > new Date().getTime();
  };

  useEffect(() => {
    if (useTaskExpectedDate && taskData?.estimatedComplete) {
      setExpectedDate(taskData.estimatedComplete);
    }
  }, [useTaskExpectedDate, taskData]);

  if (!show || !taskData) return null;

  const handleMarkInProgress = async () => {
    if (!expectedDate) {
      setAlert({
        type: "error",
        message: "Please select estimated completion date.",
      });
      return;
    }

    if (!isFutureDateTime(new Date(expectedDate))) {
      setAlert({
        type: "error",
        message: "Estimated completion date & time must be in the future.",
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        taskNo: taskData.taskNo,
        remark: remark,
        estimatedCompleteByDev: expectedDate,
      };

      const response = await ADInProgressTask(payload);
      const data = response.data;

      if (data.status === "Success") {
        setAlert({
          type: "success",
          message: data.message,
        });

        onInProgress();
        setExpectedDate("");
        setRemark("");

        setTimeout(() => {
          onClose();
          setAlert(null);
        }, 4000);
      } else {
        setAlert({
          type: "error",
          message: data.message || "Failed to update task",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message:
          error.response?.data?.message || "Failed to in progress the task.",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "Assigned":
        return {
          icon: <UserCheck size={14} className="text-indigo-700" />,
          text: "Assigned",
          style: "bg-indigo-100 text-indigo-700",
        };

      case "In Progress":
        return {
          icon: <Loader size={14} className="text-blue-700 animate-spin" />,
          text: "In Progress",
          style: "bg-blue-100 text-blue-700",
        };

      case "Completed":
        return {
          icon: <CheckCircle size={14} className="text-green-700" />,
          text: "Completed",
          style: "bg-green-100 text-green-700",
        };

      case "Re-open":
        return {
          icon: <RotateCcw size={14} className="text-purple-700" />,
          text: "Re-open",
          style: "bg-purple-100 text-purple-700",
        };

      case "Closed":
        return {
          icon: <CircleSlash size={14} className="text-gray-700" />,
          text: "Closed",
          style: "bg-gray-200 text-gray-700",
        };

      case "Hold":
        return {
          icon: <PauseCircle size={14} className="text-yellow-700" />,
          text: "On Hold",
          style: "bg-yellow-100 text-yellow-700",
        };

      default:
        return {
          icon: <FileBadge size={14} className="text-gray-600" />,
          text: status,
          style: "bg-gray-100 text-gray-600",
        };
    }
  };
  const statusInfo = getStatusInfo(taskData.status);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-end z-50 transition-opacity duration-300 opacity-100"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="bg-white  w-full max-w-xl h-full shadow-2xl border-l animate-slideFadeIn flex flex-col overflow-hidden rounded-l-2xl">
          {/* Header */}
          <div
            className="px-5 py-4 border-b flex items-center justify-between text-white shadow-md bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${frameBg})` }}
          >
            <div className="flex flex-col">
              <h2 className="text-xl font-semibold tracking-wide">
                Mark In-Progress
              </h2>

              <span className="mt-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-medium w-fit">
                #{taskData.taskNo}
              </span>
            </div>

            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition"
            >
              <X size={26} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Task Details */}
            <div className="bg-white border shadow-sm rounded-xl p-5 space-y-4 hover:shadow-md transition">
              <div className="flex items-center justify-between w-full">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FileBadge size={16} className="text-blue-600" />
                  Task Details
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusInfo.style}`}
                >
                  {statusInfo.icon}
                  {statusInfo.text}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-3 text-[0.93rem] text-gray-700">
                <p>
                  <span className="font-semibold">Description:</span>{" "}
                  {taskData.description}
                </p>

                <p>
                  <span className="font-semibold">Assigned By:</span>{" "}
                  {taskData.assignedByEmail}
                </p>

                <p>
                  <span className="font-semibold">Project:</span>{" "}
                  {taskData.projectName}
                </p>

                <p>
                  <span className="font-semibold">Expected Complete Date:</span>{" "}
                  {formatDateTime(taskData.estimatedComplete)}
                </p>

                <p>
                  <span className="font-semibold">Created At:</span>{" "}
                  {formatDateTime(taskData.createdAt)}
                </p>

                <p>
                  <span className="font-semibold">Severity:</span>{" "}
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold w-fit
                    ${
                      taskData.severity === "High"
                        ? "bg-red-100 text-red-700"
                        : taskData.severity === "Moderate"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-green-100 text-green-700"
                    }`}
                  >
                    {taskData.severity}
                  </span>
                </p>
              </div>
            </div>

            {/* Estimated Date */}
            <div className="bg-white border shadow-sm rounded-xl p-5 hover:shadow-md transition">
              <div className="flex items-center justify-between w-full">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estimated Completion <span className="text-red-500">*</span>
                </label>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={useTaskExpectedDate}
                    onChange={(e) => setUseTaskExpectedDate(e.target.checked)}
                  />
                  <span className="text-sm text-gray-600">Same as Task</span>
                </div>
              </div>

              <Flatpickr
                value={expectedDate ? new Date(expectedDate) : null}
                onReady={(selectedDates, dateStr, instance) => {
                  // force initial valid range
                  const dt = getNextAllowedScheduleDate(
                    instance.selectedDates[0] || new Date(),
                  );

                  const min = new Date(dt);
                  min.setHours(9, 30, 0, 0);

                  const max = new Date(dt);
                  max.setHours(18, 0, 0, 0);

                  instance.config.minTime = "09:30";
                  instance.config.maxTime = "18:00";
                }}
                onValueUpdate={(selectedDates, dateStr, instance) => {
                  if (!selectedDates.length) return;

                  const dt = selectedDates[0];

                  if (isBlockedScheduleDate(dt)) {
                    const nextAllowedDate = getNextAllowedScheduleDate(
                      new Date(Math.max(dt.getTime(), Date.now())),
                    );
                    nextAllowedDate.setHours(9, 30, 0, 0);
                    instance.setDate(nextAllowedDate, false);
                    return;
                  }

                  const min = new Date(dt);
                  min.setHours(9, 30, 0, 0);

                  const max = new Date(dt);
                  max.setHours(18, 0, 0, 0);

                  // if time goes below 9:30 AM → pull it back
                  if (dt < min) {
                    instance.setDate(min, false);
                  }

                  // if above 6 PM → push to next-day 9:30 AM
                  if (dt > max) {
                    const nextDay = getNextAllowedScheduleDate(
                      new Date(
                        dt.getFullYear(),
                        dt.getMonth(),
                        dt.getDate() + 1,
                      ),
                    );
                    nextDay.setHours(9, 30, 0, 0);
                    instance.setDate(nextDay, false);
                  }
                }}
                onClose={(selectedDates, dateStr, instance) => {
                  if (!selectedDates?.length) return;

                  const final = instance.selectedDates[0];

                  if (isBlockedScheduleDate(final)) {
                    setAlert({
                      type: "error",
                      message:
                        "Sundays and previous dates are not allowed for estimated completion.",
                    });

                    setExpectedDate("");
                    instance.clear();
                    return;
                  }

                  if (!isFutureDateTime(final)) {
                    setAlert({
                      type: "error",
                      message:
                        "Estimated completion date & time must be in the future.",
                    });

                    setExpectedDate("");
                    instance.clear();
                    return;
                  }

                  const localISO = new Date(
                    final.getTime() - final.getTimezoneOffset() * 60000,
                  )
                    .toISOString()
                    .slice(0, 19);

                  setExpectedDate(localISO);
                }}
                options={{
                  enableTime: true,
                  dateFormat: "d-m-Y h:i K", // show AM/PM
                  time_24hr: false,
                  minuteIncrement: 15,
                  allowInput: true,
                  minDate: "today",
                  disable: flatpickrScheduleDateDisable,

                  minTime: "09:30",
                  maxTime: "18:00",
                }}
                disabled={useTaskExpectedDate}
                className={`w-full p-3 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm 
    ${useTaskExpectedDate ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
            </div>

            {/* Remarks */}
            <div className="bg-white border shadow-sm rounded-xl p-5 hover:shadow-md transition">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Remarks <span className="text-red-500">*</span>
              </label>

              <textarea
                rows={4}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Add your remark..."
                className="w-full p-3 border rounded-lg text-sm bg-white focus:outline-none 
                       focus:ring-2 focus:ring-blue-400 shadow-sm"
              ></textarea>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 items-center shadow-inner">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm border rounded-md bg-white text-gray-700 hover:bg-gray-100 shadow-sm transition"
            >
              Cancel
            </button>

            <button
              onClick={handleMarkInProgress}
              disabled={loading}
              className="px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow-md hover:opacity-90 transition"
            >
              {loading ? "Updating..." : "Mark In-Progress"}
            </button>
          </div>
        </div>

        {alert && (
          <NotificationAlert
            type={alert.type}
            message={alert.message}
            show={true}
            duration={4000}
            onClose={() => setAlert(null)}
          />
        )}
      </div>
    </>
  );
};

export default ADInProgressTaskModal;
